import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authService, cardRequestService, waitlistService } from "@/services";
import { useTenant } from "@/lib/tenant-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Clock, CheckCircle2, XCircle, Package, RefreshCw, Gift, Copy, Share2, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const statusConfig: Record<string, { icon: any; label: string; color: string }> = {
  draft: { icon: Clock, label: "Draft", color: "bg-muted text-muted-foreground" },
  pending: { icon: Clock, label: "Pending Review", color: "bg-warning/10 text-warning" },
  approved: { icon: CheckCircle2, label: "Approved", color: "bg-success/10 text-success" },
  rejected: { icon: XCircle, label: "Rejected", color: "bg-destructive/10 text-destructive" },
  fulfilled: { icon: Package, label: "Fulfilled", color: "bg-primary/10 text-primary" },
};

interface CardRequestStatus {
  status: string;
  submitted_at: string | null;
  rejection_reason: string | null;
  reviewed_at: string | null;
}

interface WaitlistInfo {
  referral_code: string | null;
  referral_count: number;
  referral_earnings_cents: number;
  position: number | null;
}

const UserStatus = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { toast } = useToast();
  const [request, setRequest] = useState<CardRequestStatus | null>(null);
  const [waitlistInfo, setWaitlistInfo] = useState<WaitlistInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const isWaitlist = tenant?.tenantType === "waitlist";

  const fetchStatus = async () => {
    setLoading(true);
    const { data: { user } } = await authService.getUser();
    if (user) {
      const { data } = await cardRequestService.getUserLatestCardRequest(user.id);
      setRequest(data as CardRequestStatus | null);

      if (isWaitlist && tenant) {
        const { data: entry } = await waitlistService.getUserWaitlistEntry(tenant.id, user.id);
        if (entry) {
          setWaitlistInfo({
            referral_code: (entry as any).referral_code,
            referral_count: (entry as any).referral_count ?? 0,
            referral_earnings_cents: (entry as any).referral_earnings_cents ?? 0,
            position: entry.position,
          });
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
  }, [tenant]);

  const formatCents = (cents: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

  const status = request?.status || "pending";
  const cfg = statusConfig[status] || statusConfig.pending;
  const Icon = cfg.icon;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!request && !waitlistInfo) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 text-center">
        <p className="text-muted-foreground mb-4">No submission found.</p>
        <Button variant="outline" onClick={() => navigate(`/t/${tenant_slug}`)}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/t/${tenant_slug}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">Your Submission</span>
        <Button variant="ghost" size="icon" className="ml-auto" onClick={fetchStatus}>
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6 animate-fade-in">
        {/* Status section */}
        {request && (
          <>
            <div className={cn("flex h-20 w-20 items-center justify-center rounded-full", cfg.color)}>
              <Icon className="h-10 w-10" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold font-display mb-1">{cfg.label}</h1>
              <p className="text-muted-foreground text-sm">
                {request.submitted_at
                  ? `Submitted ${new Date(request.submitted_at).toLocaleDateString()}`
                  : "Not yet submitted"}
              </p>
            </div>

            {request.rejection_reason && (
              <Card className="max-w-sm w-full shadow-card">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-destructive mb-1">Rejection Reason</p>
                  <p className="text-sm text-muted-foreground">{request.rejection_reason}</p>
                </CardContent>
              </Card>
            )}

            <Card className="max-w-sm w-full shadow-card">
              <CardContent className="p-4 space-y-3">
                {["Submitted", "Under Review", "Decision", "Fulfilment"].map((s, i) => {
                  const isActive = i <= (status === "pending" ? 1 : status === "approved" ? 3 : status === "rejected" ? 2 : 0);
                  return (
                    <div key={s} className="flex items-center gap-3">
                      <div className={cn("h-3 w-3 rounded-full", isActive ? "bg-primary" : "bg-muted")} />
                      <span className={cn("text-sm", isActive ? "text-foreground font-medium" : "text-muted-foreground")}>{s}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </>
        )}

        {/* Waitlist position */}
        {waitlistInfo?.position && (
          <Card className="max-w-sm w-full shadow-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Waitlist Position</p>
                <p className="text-2xl font-bold font-display">#{waitlistInfo.position}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Referral card */}
        {waitlistInfo?.referral_code && (
          <Card className="max-w-sm w-full shadow-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Invite friends</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Share your code. For every friend who joins, you move up the waitlist.
              </p>

              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-lg bg-muted px-4 py-2.5 text-center font-mono font-bold text-lg tracking-widest">
                  {waitlistInfo.referral_code}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(waitlistInfo.referral_code!);
                    toast({ title: "Copied!", description: "Referral code copied to clipboard." });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => {
                  const shareUrl = `${window.location.origin}/t/${tenant_slug}?ref=${waitlistInfo.referral_code}`;
                  if (navigator.share) {
                    navigator.share({ title: `Join ${tenant?.brandName}`, text: `Use my code ${waitlistInfo.referral_code} to join!`, url: shareUrl });
                  } else {
                    navigator.clipboard.writeText(shareUrl);
                    toast({ title: "Link copied!", description: "Share link copied to clipboard." });
                  }
                }}
              >
                <Share2 className="h-4 w-4" /> Share Invite Link
              </Button>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold font-display">{waitlistInfo.referral_count}</p>
                  <p className="text-xs text-muted-foreground">Friends Referred</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold font-display text-primary">{formatCents(waitlistInfo.referral_earnings_cents)}</p>
                  <p className="text-xs text-muted-foreground">Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserStatus;